import React, { useEffect, useState, useRef } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import Board from './Board';
import Controls from './Controls';
import WinnerDisplay from './WinnerDisplay';
import ConfirmDialog from './ConfirmDialog';
import NameInputDialog from './NameInputDialog';
import MuteToggle from './MuteToggle';
import ThemeToggleIcon from './ThemeToggleIcon';
import Tutorial from './Tutorial';
import { lightTheme, darkTheme, createFloatingLetterEffect, floatingLettersOnlyCSS } from '../styles/theme';
import { analyticsEvents } from '../utils/analytics';

const themes = {
    light: lightTheme,
    dark: darkTheme,
};

// Create shared components (not used in GameScreen, but kept for potential future use)
// const { SharedBackgroundContainer } = createSharedComponents(styled, createGlobalStyle);

// Create a floating letters only style for GameScreen (no body background)
const FloatingLettersGlobalStyle = createGlobalStyle`
  ${floatingLettersOnlyCSS}
`;

const GameContainer = styled.div`
  text-align: center;
  font-family: ${({ theme }) => theme.fontFamily};
  width: 100%;
  position: relative;
  padding: 0 1rem;
  box-sizing: border-box;
  h1, h2, h3, h4 {
    text-shadow: ${({ theme }) => theme.textShadow};
  }
  
  @media (max-width: 500px) {
    padding: 0 1rem;
  }
`;

const DifficultySelector = styled.div`
  margin: 20px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  font-size: 1rem;
  text-shadow: 2px 2px 2px rgba(0,0,0,0.2);

  select {
    font-family: ${({ theme }) => theme.fontFamily};
    font-size: 0.8rem;
    padding: 8px 12px;
    border-radius: 8px;
    border: 3px solid ${({ theme }) => (theme.body === '#1A1A1A' ? theme.text : 'rgba(0,0,0,0.3)')};
    background-color: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  }
`;

// Removed - using SharedGlobalStyle instead

const TurnDisplay = styled.h2`
  color: ${props => props.$isMyTurn ? props.theme.tilePlayer : props.theme.tileAI};
  margin: 0.5rem;
`;

const PlayerInfo = styled.p`
  margin: 0.5rem;
`;

const WordCountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$color};
  color: white;
  font-size: 0.85rem;
  font-weight: bold;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  margin-left: 0.1rem;
  min-width: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-family: ${({ theme }) => theme.fontFamily};
`;

const CrownIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 0.3rem;
  font-size: 1rem;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
  animation: ${props => props.$animate ? 'pulse 2s ease-in-out infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
`;

const FloatingPlusOne = styled.div`
  position: fixed;
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.$color || '#fff'};
  pointer-events: none;
  z-index: 2000;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  animation: floatUp 1.5s ease-out forwards;
  font-family: ${({ theme }) => theme.fontFamily};
  
  @keyframes floatUp {
    0% {
      opacity: 1;
      transform: translate(0, 0) scale(1);
    }
    100% {
      opacity: 0;
      transform: translate(${props => props.$deltaX || 0}px, ${props => props.$deltaY || -200}px) scale(1.2);
    }
  }
`;

const BoardWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin: 0 auto;
`;

const HelpButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.4rem;
  font-weight: bold;
  color: ${({ theme }) => theme.text};
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  line-height: 1;
  box-sizing: border-box;
  margin: 0;
  vertical-align: middle;
  
  &:hover {
    transform: scale(1.06);
    background: rgba(255, 255, 255, 0.25);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: scale(0.96);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
`;

const TopBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-evenly; /* Evenly space all controls */
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0 1rem;
  box-sizing: border-box;
  
  @media (max-width: 500px) {
    padding: 0 1rem;
    gap: 0.5rem;
  }
`;

const ControlItem = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px; /* Normalize height so items align */
  @media (max-width: 768px) {
    height: 40px;
  }
`;

// Static background for GameScreen only (no gradients/animation)
const StaticBackground = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 100vh;
  padding: 2rem 0;
  background: ${({ theme }) => theme.body};
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontFamily};
`;

// Reusable component for rendering player info in Found Words section
const PlayerFoundWordsEntry = ({ 
  playerKey, 
  displayName, 
  playerColor, 
  foundWords, 
  isLeader,
  playerNameRefs,
  theme,
  themeMode
}) => {
  const textColor = themeMode === 'dark' ? lightTheme.text : theme.text;
  const backgroundColor = 'rgba(255, 255, 255, 0.8)';
  return (
    <div style={{ 
      marginBottom: '0.5rem',
      padding: '0.5rem 0.75rem',
      borderRadius: '8px',
      backgroundColor,
      width: '90%',
      maxWidth: '400px',
      borderLeft: `4px solid ${playerColor}`,
      textAlign: 'left',
      color: textColor
    }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <strong 
          ref={el => { if (el) playerNameRefs.current[playerKey] = el; }}
          style={{ color: playerColor }}
        >
          {displayName}:
        </strong>
        {isLeader && (
          <CrownIcon $animate={true} title="Leader">
            👑
          </CrownIcon>
        )}
        <WordCountPill $color={playerColor} theme={theme}>
          {foundWords.length}
        </WordCountPill>
        <span style={{ fontSize: '1.1rem', fontFamily: theme.foundWordsFont, color: textColor }}>
          {foundWords.length > 0 ? foundWords.join(', ') : ''}
        </span>
      </div>
    </div>
  );
};

const GameScreen = ({ theme, toggleTheme, gameState, handlers, setAnimationState, playerId, isMultiplayer, showCheckButton }) => {
  const [showTutorial, setShowTutorial] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [plusOneAnimations, setPlusOneAnimations] = useState([]);
  const playerNameRefs = useRef({});

  useEffect(() => {
    // Use shared floating letter effect
    const cleanup = createFloatingLetterEffect(4000, 6, 2000);
    return cleanup;
  }, []);

  // Track previous word counts to detect when words are found
  const prevWordCountsRef = useRef({});
  
  useEffect(() => {
    if (!gameState) return;
    
    const newAnimations = [];
    let currentCounts = {};
    
    // Handle multiplayer mode
    if (gameState.allPlayersData && isMultiplayer) {
      gameState.allPlayersData.forEach(player => {
        currentCounts[player.playerId] = player.foundWords.length;
      });
    } 
    // Handle single-player mode
    else if (!isMultiplayer) {
      currentCounts['player'] = gameState.playerFoundWords?.length || 0;
      currentCounts['ai'] = gameState.aiFoundWords?.length || 0;
    } else {
      return; // No data available
    }
    
    const prevCounts = prevWordCountsRef.current;
    
    // Check each player for new words
    Object.keys(currentCounts).forEach(checkPlayerId => {
      const currentCount = currentCounts[checkPlayerId] || 0;
      const prevCount = prevCounts[checkPlayerId] || 0;
      const wordsFound = currentCount - prevCount;
      
      if (wordsFound > 0) {
        // Get player color
        let playerColor;
        if (isMultiplayer && gameState.allPlayersData) {
          const player = gameState.allPlayersData.find(p => p.playerId === checkPlayerId);
          if (!player) return;
          
          const opponentColors = themes[theme].opponentColors;
          const mainPlayerColor = themes[theme].tilePlayer;
          playerColor = mainPlayerColor;
          if (!player.isCurrentPlayer) {
            const currentPlayerId = playerId;
            const allPossiblePlayers = ['player1', 'player2', 'player3', 'player4', 'player5'];
            const otherPlayers = allPossiblePlayers.filter(p => p !== currentPlayerId);
            const colorIndex = otherPlayers.indexOf(checkPlayerId);
            playerColor = colorIndex !== -1 ? opponentColors[colorIndex % opponentColors.length] : mainPlayerColor;
          }
        } else {
          // Single-player mode
          playerColor = checkPlayerId === 'player' 
            ? themes[theme].tilePlayer 
            : (themes[theme].tileAI || themes[theme].tileOpponent);
        }
        
        // Calculate positions
        const boardElement = document.querySelector('[data-board-wrapper]');
        const playerNameKey = checkPlayerId === 'player' ? 'You' : (checkPlayerId === 'ai' ? 'AI' : checkPlayerId);
        const playerNameElement = playerNameRefs.current[playerNameKey];
        
        if (boardElement && playerNameElement) {
          const boardRect = boardElement.getBoundingClientRect();
          const nameRect = playerNameElement.getBoundingClientRect();
          
          const startX = boardRect.left + boardRect.width / 2;
          const startY = boardRect.top + boardRect.height / 2;
          const endX = nameRect.left + nameRect.width / 2;
          const endY = nameRect.top + nameRect.height / 2;
          
          const deltaX = endX - startX;
          const deltaY = endY - startY;
          
          // Create animation for each word found
          for (let i = 0; i < wordsFound; i++) {
            newAnimations.push({
              id: `${checkPlayerId}-${Date.now()}-${i}`,
              playerId: checkPlayerId,
              startX,
              startY,
              deltaX,
              deltaY,
              color: playerColor,
            });
          }
        }
      }
    });
    
    // Update previous counts
    prevWordCountsRef.current = currentCounts;
    
    // Add new animations
    if (newAnimations.length > 0) {
      setPlusOneAnimations(prev => [...prev, ...newAnimations]);
      
      // Remove animations after they complete
      setTimeout(() => {
        setPlusOneAnimations(prev => prev.filter(anim => !newAnimations.find(a => a.id === anim.id)));
      }, 1500);
    }
  }, [gameState, isMultiplayer, theme, playerId]);

  const handleShowTutorial = () => {
    setShowTutorial(true);
    analyticsEvents.helpButtonClicked(isMultiplayer ? 'multiplayer_game' : 'single_game');
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
  };

  // Safety check to prevent crash before game state is initialized
  if (!gameState || !gameState.grid || gameState.grid.length === 0) {
        return <div>Loading...</div>;
    }

    // Ensure 'You' appears first in the Found Words section
    const computedPlayers = gameState.allPlayersData
      ? [...gameState.allPlayersData].sort((a, b) => (a.isCurrentPlayer === b.isCurrentPlayer ? 0 : a.isCurrentPlayer ? -1 : 1))
      : [];
    
    // Determine the leader (player with most found words)
    // Only show crown if there's a clear leader (no ties)
    const maxWords = computedPlayers.length > 0 
      ? Math.max(...computedPlayers.map(p => p.foundWords.length))
      : 0;
    const playersWithMaxWords = computedPlayers.filter(p => p.foundWords.length === maxWords && maxWords > 0);
    const leaderIds = playersWithMaxWords.length === 1
      ? playersWithMaxWords.map(p => p.playerId)
      : []; // No crown if tied

    return (
        <ThemeProvider theme={themes[theme]}>
            <>
                <FloatingLettersGlobalStyle />
                <StaticBackground>
                    <TopBar>
                        <ControlItem>
                          <ThemeToggleIcon theme={theme} toggleTheme={toggleTheme} />
                        </ControlItem>
                        <ControlItem>
                          <HelpButton theme={themes[theme]} onClick={handleShowTutorial}>?</HelpButton>
                        </ControlItem>
                        <ControlItem>
                          <HelpButton as="a" href="/heist/" theme={themes[theme]} title="Home">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </HelpButton>
                        </ControlItem>
                        <ControlItem>
                          <MuteToggle />
                        </ControlItem>
                    </TopBar>
                    <GameContainer>
                    {!isMultiplayer && (
                        <DifficultySelector>
                            <label htmlFor="difficulty">AI Difficulty:</label>
                            <select id="difficulty" value={gameState.aiDifficulty} onChange={handlers.handleDifficultyChange}>
                                <option value="easy">Easy</option>
                                <option value="medium">Medium</option>
                                <option value="hard">Hard</option>
                            </select>
                        </DifficultySelector>
                    )}
                    {/* <h1>Letter Heist</h1> */}
                    {isMultiplayer && (
                        <div>
                            <PlayerInfo>
                                You are: {gameState.names && gameState.names[playerId] ? gameState.names[playerId] : playerId}
                                <button
                                  onClick={() => {
                                    setShowNameDialog(true);
                                  }}
                                  aria-label="Edit name"
                                  title="Edit name"
                                  style={{
                                    marginLeft: '0.2rem',
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '2.4rem',
                                    fontWeight: 'bold',
                                    color: themes[theme].text,
                                    cursor: 'pointer',
                                    // display: 'inline-flex',
                                    alignItems: 'end-left'
                                  }}
                                >
                                  ✎
                                </button>
                            </PlayerInfo>
                            <TurnDisplay $isMyTurn={gameState.turn === playerId}>
                                {gameState.turn === playerId 
                                    ? "Your Turn" 
                                    : `${(gameState.names && gameState.names[gameState.turn]) ? gameState.names[gameState.turn] : gameState.turn}'s Turn`}
                            </TurnDisplay>
                        </div>
                    )}
                    {/* REMOVED: No more scoring, just count words
                    <p>
                        Player Score: {gameState.playerScore} | 
                        {isMultiplayer ? ' Opponent' : ' AI'} Score: {gameState.aiScore}
                    </p>
                    */}
                    {gameState.grid.length > 0 && (
                        <BoardWrapper data-board-wrapper>
                            <Board
                                grid={gameState.grid}
                                onTileClick={handlers.handleTileClick}
                                selectedTiles={gameState.selectedTiles}
                                animationState={gameState.animationState}
                                setAnimationState={setAnimationState}
                                playerFoundWords={gameState.playerFoundWords}
                                playerId={playerId} // Pass playerId down to Board
                                isProcessingMove={gameState.isProcessingMove}
                                totalPlayers={gameState.totalPlayers || 2} // Pass total player count
                            />
                        </BoardWrapper>
                    )}
                    {/* Help button moved to TopControls */}
                    {gameState.showConfirmDialog && (
                        <ConfirmDialog
                            message="Are you sure you want to swap these two letters?"
                            onConfirm={handlers.handleConfirmSwap}
                            onCancel={handlers.handleCancelSwap}
                        />
                    )}
                    {gameState.showConfirmCheck && (
                        <ConfirmDialog
                            message="Are you sure you want to check this word?"
                            onConfirm={handlers.handleConfirmCheckWord}
                            onCancel={handlers.handleCancelCheckWord}
                        />
                    )}
                    {showNameDialog && (
                        <NameInputDialog
                            message="Enter your display name (max 20 chars):"
                            initialValue={(gameState.names && gameState.names[playerId]) ? gameState.names[playerId] : playerId}
                            onConfirm={(newName) => {
                                handlers.handleChangePlayerName(newName);
                                setShowNameDialog(false);
                            }}
                            onCancel={() => setShowNameDialog(false)}
                            theme={themes[theme]}
                        />
                    )}
                    <Controls
                        onCheckWord={handlers.handleCheckWord}
                        playerTurn={gameState.playerTurn}
                        isCheckMode={gameState.isCheckMode}
                        isProcessingMove={gameState.isProcessingMove}
                        isMultiplayer={isMultiplayer}
                        showCheckButton={showCheckButton}
                    />                    
                    {/* <h4>Found Words</h4> */}
                    {gameState.allPlayersData && isMultiplayer ? (
                        // Display all players (2-5 players)
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                            {computedPlayers.map((player, index) => {
                                // Determine player color using same logic as Tile.js
                                const opponentColors = themes[theme].opponentColors;
                                const mainPlayerColor = themes[theme].tilePlayer;
                                
                                let playerColor = mainPlayerColor;
                                if (!player.isCurrentPlayer) {
                                    // Use same logic as getPlayerOpponentColor in Tile.js
                                    const currentPlayerId = playerId;
                                    const allPossiblePlayers = ['player1', 'player2', 'player3', 'player4', 'player5'];
                                    const otherPlayers = allPossiblePlayers.filter(p => p !== currentPlayerId);
                                    const colorIndex = otherPlayers.indexOf(player.playerId);
                                    playerColor = opponentColors[colorIndex % opponentColors.length];
                                }
                                
                                const displayName = player.isCurrentPlayer 
                                  ? 'You' 
                                  : `${(gameState.names && gameState.names[player.playerId]) ? gameState.names[player.playerId] : player.playerId}`;
                                
                                return (
                                  <PlayerFoundWordsEntry
                                    key={player.playerId}
                                    playerKey={player.playerId}
                                    displayName={displayName}
                                    playerColor={playerColor}
                                    foundWords={player.foundWords}
                                    isLeader={leaderIds.includes(player.playerId)}
                                    playerNameRefs={playerNameRefs}
                                  theme={themes[theme]}
                                  themeMode={theme}
                                  />
                                );
                            })}
                        </div>
                    ) : (
                        // Single-player boxed layout matching multiplayer style
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                            {[
                              { playerKey: 'You', actualKey: 'player', words: gameState.playerFoundWords, color: themes[theme].tilePlayer },
                              { playerKey: 'AI', actualKey: 'ai', words: gameState.aiFoundWords, color: themes[theme].tileAI || themes[theme].tileOpponent }
                            ].map((entry) => {
                              // Determine leader in single-player (player with most words, only if there's a clear leader)
                              const playerWords = gameState.playerFoundWords.length;
                              const aiWords = gameState.aiFoundWords.length;
                              const isLeader = entry.words.length > 0 && 
                                (entry.playerKey === 'You' ? playerWords > aiWords : aiWords > playerWords);
                              
                              return (
                                <PlayerFoundWordsEntry
                                  key={entry.playerKey}
                                  playerKey={entry.playerKey}
                                  displayName={entry.playerKey}
                                  playerColor={entry.color}
                                  foundWords={entry.words}
                                  isLeader={isLeader}
                                  playerNameRefs={playerNameRefs}
                                  theme={themes[theme]}
                                  themeMode={theme}
                                />
                              );
                            })}
                        </div>
                    )}
                    {gameState.gameOver && <h2>Game Over</h2>}
                    {gameState.gameOver && gameState.winner && <WinnerDisplay winner={gameState.winner} isMultiplayer={isMultiplayer} />}
                    {/* MuteToggle moved to TopControls */}
                    </GameContainer>
                    {/* Floating +1 animations */}
                    {plusOneAnimations.map(anim => (
                      <FloatingPlusOne
                        key={anim.id}
                        $color={anim.color}
                        $deltaX={anim.deltaX}
                        $deltaY={anim.deltaY}
                        theme={themes[theme]}
                        style={{
                          left: `${anim.startX}px`,
                          top: `${anim.startY}px`,
                        }}
                      >
                        +1
                      </FloatingPlusOne>
                    ))}
                    <Tutorial 
                  isVisible={showTutorial} 
                  onComplete={handleTutorialComplete} 
                  theme={themes[theme]}
                />
                </StaticBackground>
            </>
        </ThemeProvider>
    );
};

export default GameScreen; 